import { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Pencil,
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  Phone,
  Mail,
  Paperclip,
  ExternalLink,
  Download,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useDeleteAgreement } from './useAgreements';
import { getAgreementPaymentSignedUrl, useDeleteAgreementPayment } from './useAgreementPayments';
import { AgreementModal } from './AgreementModal';
import { PaymentModal } from './PaymentModal';
import type { Agreement, AgreementPayment } from './types';

function formatBrl(n: number): string {
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatRefMonth(refMonth: string): string {
  // refMonth = "YYYY-MM"
  const [y, m] = refMonth.split('-');
  if (!y || !m) return refMonth;
  const d = new Date(Number(y), Number(m) - 1, 1);
  return format(d, "MMMM 'de' yyyy", { locale: ptBR });
}

function PaymentRow({ payment }: { payment: AgreementPayment }) {
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const remove = useDeleteAgreementPayment();

  const openInNewTab = async () => {
    if (!payment.file_path) return;
    setBusy(true);
    const url = await getAgreementPaymentSignedUrl(payment.file_path);
    setBusy(false);
    if (!url) {
      toast.error('Não foi possível gerar o link.');
      return;
    }
    window.open(url, '_blank', 'noreferrer');
  };

  const downloadFile = async () => {
    if (!payment.file_path) return;
    setBusy(true);
    try {
      const url = await getAgreementPaymentSignedUrl(payment.file_path);
      if (!url) throw new Error('Sem URL.');
      const res = await fetch(url);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      const filename =
        payment.file_path
          .split('/')
          .pop()
          ?.replace(/^[^_]+_/, '') ?? 'repasse';
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      toast.error(`Erro ao baixar: ${e instanceof Error ? e.message : 'desconhecido'}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="grid grid-cols-12 items-center gap-2 border-t border-border/50 px-3 py-2 text-xs">
        <div className="col-span-3">
          <p className="font-medium text-foreground">{formatRefMonth(payment.reference_month)}</p>
          <p className="text-muted-foreground">
            {format(new Date(`${payment.payment_date}T00:00:00`), 'dd/MM/yyyy', { locale: ptBR })}
          </p>
        </div>
        <div className="col-span-2 text-right tabular-nums">
          {formatBrl(Number(payment.gross_amount))}
        </div>
        <div className="col-span-2 text-right tabular-nums text-expense">
          {Number(payment.glosa_amount) > 0 ? `− ${formatBrl(Number(payment.glosa_amount))}` : '—'}
        </div>
        <div className="col-span-2 text-right font-semibold tabular-nums text-income">
          {formatBrl(Number(payment.net_amount))}
        </div>
        <div className="col-span-3 flex items-center justify-end gap-1">
          {payment.file_path && (
            <>
              <Button
                size="icon"
                variant="ghost"
                onClick={openInNewTab}
                disabled={busy}
                className="h-6 w-6"
                title="Abrir anexo"
              >
                {busy ? <Loader2 size={11} className="animate-spin" /> : <ExternalLink size={11} />}
              </Button>
              <Button
                size="icon"
                variant="ghost"
                onClick={downloadFile}
                disabled={busy}
                className="h-6 w-6"
                title="Baixar"
              >
                <Download size={11} />
              </Button>
            </>
          )}
          {!payment.file_path && (
            <span className="text-xs text-muted-foreground/60" title="Sem anexo">
              <Paperclip size={11} className="opacity-30" />
            </span>
          )}
          <Button
            size="icon"
            variant="ghost"
            onClick={() => setConfirmDelete(true)}
            disabled={remove.isPending}
            className="h-6 w-6 text-muted-foreground hover:text-expense"
            title="Excluir"
          >
            <Trash2 size={11} />
          </Button>
        </div>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir repasse?</AlertDialogTitle>
            <AlertDialogDescription>
              {payment.transaction_id
                ? 'A transação financeira vinculada também será removida.'
                : 'Este registro será removido do histórico do convênio.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => remove.mutate(payment)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

interface Props {
  agreement: Agreement;
  payments: AgreementPayment[];
}

export function AgreementCard({ agreement, payments }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const remove = useDeleteAgreement();

  const stats = useMemo(() => {
    const ps = payments.filter((p) => p.agreement_id === agreement.id);
    const totalGross = ps.reduce((s, p) => s + Number(p.gross_amount), 0);
    const totalGlosa = ps.reduce((s, p) => s + Number(p.glosa_amount), 0);
    const totalNet = ps.reduce((s, p) => s + Number(p.net_amount), 0);
    const lastDate = ps.length > 0 ? ps[0]!.payment_date : null;
    return {
      payments: ps,
      count: ps.length,
      totalGross,
      totalGlosa,
      totalNet,
      glosaPct: totalGross > 0 ? (totalGlosa / totalGross) * 100 : 0,
      lastDate,
    };
  }, [payments, agreement.id]);

  return (
    <>
      <Card className={agreement.is_active ? '' : 'opacity-70'}>
        <CardContent className="p-0">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex w-full items-start justify-between gap-3 p-4 text-left hover:bg-secondary/30"
          >
            <div className="flex items-start gap-3">
              {expanded ? (
                <ChevronDown size={16} className="mt-1 text-muted-foreground" />
              ) : (
                <ChevronRight size={16} className="mt-1 text-muted-foreground" />
              )}
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-foreground">{agreement.name}</h3>
                  {agreement.operator && (
                    <span className="text-xs text-muted-foreground">· {agreement.operator}</span>
                  )}
                  {!agreement.is_active && (
                    <span className="rounded bg-secondary px-1.5 py-0.5 text-xs text-muted-foreground">
                      Inativo
                    </span>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                  {agreement.contact_phone && (
                    <span className="inline-flex items-center gap-1">
                      <Phone size={11} />
                      {agreement.contact_phone}
                    </span>
                  )}
                  {agreement.contact_email && (
                    <span className="inline-flex items-center gap-1">
                      <Mail size={11} />
                      {agreement.contact_email}
                    </span>
                  )}
                  {stats.lastDate && (
                    <span>
                      Último repasse:{' '}
                      {format(new Date(`${stats.lastDate}T00:00:00`), 'dd/MM/yyyy', {
                        locale: ptBR,
                      })}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-baseline gap-3">
              <div className="text-right">
                <p className="text-xs text-muted-foreground">
                  {stats.count} repasse{stats.count === 1 ? '' : 's'}
                </p>
                <p className="text-sm font-semibold tabular-nums text-income">
                  {formatBrl(stats.totalNet)}
                </p>
              </div>
            </div>
          </button>

          {expanded && (
            <div className="border-t border-border bg-secondary/20 px-4 py-3">
              <div className="mb-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-3 text-xs">
                  {stats.totalGlosa > 0 && (
                    <span className="text-muted-foreground">
                      Glosa total:{' '}
                      <span className="text-expense">{formatBrl(stats.totalGlosa)}</span> (
                      {stats.glosaPct.toFixed(1).replace('.', ',')}%)
                    </span>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPaymentOpen(true)}
                    className="h-7 text-xs"
                  >
                    <Plus size={12} className="mr-1" />
                    Registrar repasse
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditOpen(true)}
                    className="h-7 text-xs"
                  >
                    <Pencil size={12} className="mr-1" />
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setConfirmDelete(true)}
                    disabled={remove.isPending}
                    className="h-7 text-xs text-muted-foreground hover:text-expense"
                  >
                    <Trash2 size={12} />
                  </Button>
                </div>
              </div>

              {agreement.notes && (
                <p className="mb-3 rounded border border-border bg-white p-2 text-xs text-muted-foreground">
                  {agreement.notes}
                </p>
              )}

              {stats.payments.length === 0 ? (
                <p className="py-4 text-center text-xs text-muted-foreground">
                  Sem repasses registrados ainda.
                </p>
              ) : (
                <div className="rounded border border-border bg-white">
                  <div className="grid grid-cols-12 gap-2 border-b border-border bg-secondary/40 px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    <div className="col-span-3">Mês / Recebimento</div>
                    <div className="col-span-2 text-right">Bruto</div>
                    <div className="col-span-2 text-right">Glosa</div>
                    <div className="col-span-2 text-right">Líquido</div>
                    <div className="col-span-3"></div>
                  </div>
                  {stats.payments.map((p) => (
                    <PaymentRow key={p.id} payment={p} />
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <AgreementModal open={editOpen} onOpenChange={setEditOpen} edit={agreement} />
      <PaymentModal
        open={paymentOpen}
        onOpenChange={setPaymentOpen}
        agreementId={agreement.id}
        agreementName={agreement.name}
      />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir convênio?</AlertDialogTitle>
            <AlertDialogDescription>
              "{agreement.name}" será removido. Repasses já registrados ficam preservados no
              histórico financeiro.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => remove.mutate(agreement.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
